import React from 'react';
import './ModernIndicators.css';

// Types for the new indicator components
export type IndicatorPriority = 'urgent' | 'high' | 'medium' | 'low';
export type IndicatorStatus = 'active' | 'inactive' | 'pending' | 'completed';
export type IndicatorRole = 'super_agent' | 'master_agent' | 'agent' | 'customer';

interface PriorityIndicatorProps {
  priority: IndicatorPriority;
  showIcon?: boolean;
  compact?: boolean;
}

interface StatusIndicatorProps {
  status: IndicatorStatus;
  label?: string;
  showIcon?: boolean;
}

interface TagIndicatorProps {
  label: string;
  variant?: 'default' | 'subtle' | 'outlined';
}

interface RoleIndicatorProps {
  role: IndicatorRole;
  showIcon?: boolean;
}

interface CountIndicatorProps {
  count: number;
  label?: string;
}

// Priority Indicator Component
export const PriorityIndicator: React.FC<PriorityIndicatorProps> = ({ 
  priority, 
  showIcon = true, 
  compact = false 
}) => {
  const getIcon = () => {
    switch (priority) {
      case 'urgent': return 'pi pi-exclamation-triangle';
      case 'high': return 'pi pi-arrow-up';
      case 'medium': return 'pi pi-minus';
      case 'low': return 'pi pi-arrow-down';
      default: return 'pi pi-info-circle';
    }
  };

  // Ensure priority is a string before calling toUpperCase
  const priorityStr = String(priority || 'medium');

  return (
    <div className={`priority-indicator priority-${priority} ${compact ? 'compact' : ''}`}>
      {showIcon && <i className={getIcon()}></i>}
      <span>{priorityStr.toUpperCase()}</span>
    </div>
  );
};

// Status Indicator Component
export const StatusIndicator: React.FC<StatusIndicatorProps> = ({ 
  status, 
  label, 
  showIcon = true 
}) => {
  const getIcon = () => {
    switch (status) {
      case 'active': return 'pi pi-check-circle';
      case 'inactive': return 'pi pi-times-circle';
      case 'pending': return 'pi pi-clock';
      case 'completed': return 'pi pi-verified';
      default: return 'pi pi-info-circle';
    }
  };

  return (
    <div className={`status-indicator status-${status}`}>
      {showIcon && <i className={getIcon()}></i>}
      <span>{label || status.charAt(0).toUpperCase() + status.slice(1)}</span>
    </div>
  );
};

// Tag Indicator Component
export const TagIndicator: React.FC<TagIndicatorProps> = ({ 
  label, 
  variant = 'default' 
}) => {
  return (
    <span className={`tag-indicator tag-${variant}`}>
      {label}
    </span>
  );
};

// Role Indicator Component
export const RoleIndicator: React.FC<RoleIndicatorProps> = ({ 
  role, 
  showIcon = true 
}) => {
  const getIcon = () => {
    switch (role) {
      case 'super_agent': return 'pi pi-star';
      case 'master_agent': return 'pi pi-crown';
      case 'agent': return 'pi pi-user';
      case 'customer': return 'pi pi-users';
      default: return 'pi pi-user';
    }
  };

  const getLabel = () => {
    switch (role) {
      case 'super_agent': return 'Super Agent';
      case 'master_agent': return 'Master Agent';
      case 'agent': return 'Agent';
      case 'customer': return 'Customer';
      default: return role;
    }
  };

  return (
    <div className={`role-indicator role-${role}`}>
      {showIcon && <i className={getIcon()}></i>}
      <span>{getLabel()}</span>
    </div>
  );
};

// Count Indicator Component
export const CountIndicator: React.FC<CountIndicatorProps> = ({ 
  count, 
  label 
}) => {
  return (
    <span className="count-indicator">
      {label ? `${count} ${label}` : `+${count}`}
    </span>
  );
};

// Composite components for specific use cases
export const InsightPriorityIndicator: React.FC<{ priority: string | number | null | undefined }> = ({ priority }) => {
  // Normalize priority to a valid string value
  const normalizedPriority = String(priority || 'medium').toLowerCase();
  const validPriority = ['urgent', 'high', 'medium', 'low'].includes(normalizedPriority) 
    ? normalizedPriority 
    : 'medium';
  
  return <PriorityIndicator priority={validPriority as IndicatorPriority} />;
};

export const AgentRoleIndicator: React.FC<{ role: string }> = ({ role }) => {
  const mappedRole = role === 'master_agent' ? 'master_agent' : 'agent';
  return <RoleIndicator role={mappedRole} />;
};

export const CustomerTagsDisplay: React.FC<{ tags: string[]; maxVisible?: number }> = ({ 
  tags, 
  maxVisible = 3 
}) => {
  if (!tags || tags.length === 0) return null;

  const visibleTags = tags.slice(0, maxVisible);
  const remainingCount = tags.length - maxVisible;

  return (
    <div className="tags-container">
      {visibleTags.map((tag, index) => (
        <TagIndicator key={index} label={tag} variant="outlined" />
      ))}
      {remainingCount > 0 && (
        <CountIndicator count={remainingCount} />
      )}
    </div>
  );
};
